import {
  subDays,
  subWeeks,
  subMonths,
  subYears,
  startOfDay,
  startOfYear,
  endOfDay,
  endOfYear,
  eachMonthOfInterval,
  format,
  parse,
  isValid,
} from "date-fns"
import type { DateRangeMode, DateRangeValue } from "./dateRangePicker.types"

export type DateRangePreset = {
  label: string
  getDate(): Date
}

const PRESETS: DateRangePreset[] = [
  { label: "Now", getDate: () => new Date() },
  { label: "1 day", getDate: () => subDays(new Date(), 1) },
  { label: "1 week", getDate: () => subWeeks(new Date(), 1) },
  { label: "1 month", getDate: () => subMonths(new Date(), 1) },
  { label: "1 year", getDate: () => subYears(new Date(), 1) },
]

export function getDateRangePresets() {
  return PRESETS
}

export function applyDateRangePreset(
  mode: DateRangeMode,
  preset: DateRangePreset,
): DateRangeValue {
  const date = preset.getDate()
  const now = new Date()

  switch (mode) {
    case "within": {
      return {
        mode: "within",
        start: startOfDay(date).toISOString(),
        end: endOfDay(now).toISOString(),
        label: `Last ${preset.label}`,
      }
    }
    case "before": {
      return {
        mode: "before",
        end: endOfDay(date).toISOString(),
        label: `Before ${preset.label} ago`,
      }
    }
    case "after": {
      return {
        mode: "after",
        start: startOfDay(date).toISOString(),
        label: `After ${preset.label} ago`,
      }
    }
  }
}

const LABEL_FORMAT = "MMM d, yyyy"

export function formatDateRangeLabel(value: DateRangeValue): string {
  if (value.label) return value.label

  switch (value.mode) {
    case "within": {
      const start = value.start
        ? format(new Date(value.start), LABEL_FORMAT)
        : "?"
      const end = value.end ? format(new Date(value.end), LABEL_FORMAT) : "?"
      return `${start} – ${end}`
    }
    case "before": {
      const end = value.end ? format(new Date(value.end), LABEL_FORMAT) : "?"
      return `Before ${end}`
    }
    case "after": {
      const start = value.start
        ? format(new Date(value.start), LABEL_FORMAT)
        : "?"
      return `After ${start}`
    }
  }
}

export const DATE_INPUT_FORMAT = "dd.MM.yyyy"
export const DATE_INPUT_PLACEHOLDER = "dd.mm.yyyy"

export const YEARS = (() => {
  const currentYear = new Date().getFullYear()
  return Array.from(
    { length: currentYear - 1900 + 2 },
    (_, i) => currentYear + 1 - i,
  )
})()

export function getMonthOptions(year: number) {
  return eachMonthOfInterval({
    start: startOfYear(new Date(year, 0, 1)),
    end: endOfYear(new Date(year, 0, 1)),
  })
}

export function tryParseDate(input: string): Date | undefined {
  if (!input || input.length !== 10) return undefined
  const parsed = parse(input, DATE_INPUT_FORMAT, new Date())
  return isValid(parsed) ? parsed : undefined
}

export function formatDateInput(date: Date | undefined): string {
  return date ? format(date, DATE_INPUT_FORMAT) : ""
}
